import { CheckOutlined, LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons'
import { useDebounce } from 'ahooks'
import { AutoComplete, Button, Tag, message } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGetFrequentHobbiesQuery, useGetHobbySearchQuery, useHobbyCreateMutation } from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import s from '../onboarding.module.less'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage4: React.FC<OnboardingPageProps> = ({
  onPrevious,
  onNext,
  viewer,
  userInfo,
  onUserInfoChange,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, { wait: 500 })
  const userId = viewer.userId

  const [pageStartTime, setPageStartTime] = React.useState(moment())

  const { data: frequentHobbyData } = useGetFrequentHobbiesQuery({
    skip: userInfo.hobbies.length > 0,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        top: 24,
      },
      userId,
    },
  })

  const { data } = useGetHobbySearchQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      input: {
        search: debouncedSearch,
      },
      userId,
    },
  })

  const [hobbyCreate] = useHobbyCreateMutation({
    onCompleted(data) {
      const hobby = data.hobbyCreate
      if (hobby) {
        onUserInfoChange((draft) => {
          draft.hobbies = [...draft.hobbies.slice(), { ...hobby, isSelected: true }]
        })

        message.info('添加爱好成功')
      } else {
        message.warn('添加爱好失败，可能原因：已存在')
      }
    },
  })

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '4',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  React.useEffect(() => {
    if (frequentHobbyData) {
      onUserInfoChange((draft) => {
        draft.hobbies = frequentHobbyData.hobbies.map((hobby) => ({ ...hobby, isSelected: false }))
      })
    }
  }, [frequentHobbyData])

  const hobbyCandidates = data?.hobbies.slice(0, 6) || []

  const options =
    hobbyCandidates.length > 0
      ? hobbyCandidates.map((value) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <span>{value.name}</span>
            </div>
          ),
          value: value.hobbyId,
        }))
      : [{ label: <span>{search}</span>, value: 0 }]

  const searchComponent = (
    <AutoComplete
      defaultActiveFirstOption={true}
      filterOption={false}
      value={search}
      placeholder={t('user.info.hobby.searchPlaceholder')}
      style={{ width: '100%' }}
      onSelect={(id: any) => {
        if (Number(id) === 0 && search !== '') {
          hobbyCreate({
            variables: {
              name: search,
            },
          })
          return
        }

        const candidate = hobbyCandidates.find((candidate) => candidate.hobbyId === Number(id))
        if (candidate) {
          setSearch(candidate.name)
          // 选择的技能已在添加列表
          if (userInfo.hobbies.some((hobby) => hobby.hobbyId === Number(id))) {
            onUserInfoChange((draft) => {
              const hobby = draft.hobbies.find((hobby) => hobby.hobbyId === Number(id))
              hobby!.isSelected = !hobby!.isSelected
            })
          } else {
            onUserInfoChange((draft) => {
              draft.hobbies.push({
                ...hobbyCandidates.find((candidate) => candidate.hobbyId === Number(id))!,
                isSelected: true,
              })
            })
          }
        }
      }}
      options={options}
      onSearch={(value) => {
        setSearch(value)
      }}
    />
  )

  const candidates = (
    <div className={s.candidates}>
      {userInfo.hobbies.map((hobby) => {
        const isSelected = userInfo.hobbies.some((h) => h.hobbyId === hobby.hobbyId && h.isSelected)
        const Icon = isSelected ? CheckOutlined : PlusOutlined
        return (
          <Tag
            color={isSelected ? '#48bbb5' : undefined}
            style={{ backgroundColor: isSelected ? '#48bbb5' : undefined }}
            onClick={() => {
              onUserInfoChange((draft) => {
                const selectedSkill = draft.hobbies.find((h) => h.hobbyId === hobby.hobbyId)
                selectedSkill!.isSelected = !selectedSkill!.isSelected
              })
            }}
            key={hobby.hobbyId}
          >
            <div style={{ padding: 4 }}>
              {hobby.name}
              <Icon style={{ marginLeft: 8 }} />
            </div>
          </Tag>
        )
      })}
    </div>
  )

  const left = (
    <div className={s.page4}>
      {searchComponent}
      {candidates}
    </div>
  )

  const footer = (
    <>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_prev_page, {
            event_category: category.event_category,
            event_label: '4',
          })
          onPrevious!()
        }}
      >
        <LeftOutlined />
        {t('onboarding.previous')}
      </Button>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_next_page, {
            event_category: category.event_category,
            event_label: '4',
          })
          onNext!()
        }}
      >
        {t('onboarding.next')}
        <RightOutlined />
      </Button>
    </>
  )

  return (
    <OnboardingPageLayout
      header='ABOUT ME'
      subHeader={t('onboarding.page4.hobby.addTag')}
      left={left}
      footer={footer}
      percent={4}
    />
  )
}
